import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { flashErrorMessage } from '../../../actions/flash';
import { fetchM } from '../../../actions/fetchM';
import config from '../../../../config';
import { TRANSLATION_FILES } from '../../../constants/translation';

class Project extends Component {
  static propTypes = {
    params: PropTypes.shape({
      id: PropTypes.string,
      unitID: PropTypes.string,
    }),
    children: PropTypes.node,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.object,
    }),
  }
  static contextTypes = {
    store: PropTypes.object,
  }

  constructor(props) {
    super(props);
    this.state = {
      token: null,
      loading: true,
      data: null,
      unitCustomSettingsLoading: false,
      unitCustomFieldSettings: [],
      packagePricingCalculation: [],
      calculatedFeePackages: [],
    };
  }
  componentDidMount() {
    this.fetchProjectUnit();
    this.fetchMultiplePackagePricingCalculation();
    this.fetchMultiplePackageFees();
    this.fetchUnitCustomFieldSettings();
  }
  componentWillReceiveProps(nextProps) {
    const { pathname } = nextProps.location;
    if (!pathname.includes('/edit/')) {
      if (pathname !== this.props.location.pathname) {
        this.fetchProjectUnit();
      }
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  setUnitCustomSettingsLoading(unitCustomSettingsLoading = false) {
    return new Promise((res) => {
      this.setState({ unitCustomSettingsLoading }, () => {
        res(unitCustomSettingsLoading);
      });
    });
  }
  async fetchProjectUnit() {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM(`/projects/${this.props.params.id}/units/${this.props.params.unitID}/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page') }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      const data = await res.json();
      // handle token.. etc
      await this.setComponentState({
        token,
        data,
      });
    }
    await this.setLoading(false);
  }
  async fetchMultiplePackagePricingCalculation() {
    const { store } = this.context;
    const { t } = this.props;
    let res = null;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/projects/${this.props.params.id}/units/${this.props.params.unitID}/cal_multiple_package_pricing/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (err) {
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return null;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again')}.` }));
      return null;
    }
    const json = await res.json();
    await this.setComponentState({
      packagePricingCalculation: json,
    });
    return null;
  }
  async fetchMultiplePackageFees() {
    const { store } = this.context;
    let res = null;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/projects/${this.props.params.id}/units/${this.props.params.unitID}/cal_multiple_package_fee/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (err) {
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return null;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: 'There was an error with your request. Please try again.' }));
      return null;
    }
    const json = await res.json();
    await this.setComponentState({
      calculatedFeePackages: json,
    });
    return null;
  }
  async fetchUnitCustomFieldSettings() {
    await this.setUnitCustomSettingsLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM('/settings/unit_custom_fields/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (err) {
      await this.setUnitCustomSettingsLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again')}.` }));
    }
    if (!res.ok) {
      await this.setUnitCustomSettingsLoading(false);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again')}.` }));
    } else {
      const unitCustomFieldSettingsData = await res.json();
      await this.setComponentState({
        unitCustomFieldSettings: unitCustomFieldSettingsData,
      });
      await this.setUnitCustomSettingsLoading(false);
    }
  }
  newFetch = async () => {
    await this.fetchProjectUnit();
    await this.fetchMultiplePackagePricingCalculation();
    await this.fetchMultiplePackageFees();
    await this.fetchUnitCustomFieldSettings();
  }
  renderChild() {
    return Children.map(this.props.children, child => (
      React.cloneElement(child, {
        token: this.state.token,
        loading: this.state.loading || this.state.unitCustomSettingsLoading,
        data: this.state.data,
        packagePricingCalculation: this.state.packagePricingCalculation,
        calculatedFeePackages: this.state.calculatedFeePackages,
        id: this.props.params.id,
        unitID: this.props.params.unitID,
        location: this.props.location,
        routerParams: this.props.params || null,
        newFetch: this.newFetch,
        unitCustomFieldSettings: this.state.unitCustomFieldSettings,
      })
    ));
  }
  render() {
    return (
      <div>
        {this.renderChild()}
      </div>
    );
  }
}

export default withTranslation(TRANSLATION_FILES.translation)(Project);



// WEBPACK FOOTER //
// ./src/containers/Project/Unit/Unit.jsx